import React from 'react';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import showdown from 'showdown';
import parse, {
    attributesToProps,
    DOMNode,
    domToReact,
    Element,
    HTMLReactParserOptions,
    Text,
} from 'html-react-parser';

/**
 * The one home for showing markup that someone other than this code wrote (security unit 7, MI-29778): a model's
 * answer and its blocks, an outside service's block, a learning or glossary text, an AI Insight narrative, a welcome
 * text. Every display sink in the Concierge, AI Insight, report LLM and glossary paths calls this module instead of
 * mounting markup itself or importing a markup library (src/__tests__/tools/safeHtml/sinkScan.test.ts holds that).
 *
 * The rules, each over a typed fact of the parsed DOM, never a string match over the markup (CLAUDE.md rule 9):
 * - no element that loads another document, changes the page, or takes input, except a checkbox (the legacy Python
 *   service's status badge, decision U7-c), so a fake sign-in form becomes its words;
 * - no event handler and no srcdoc page;
 * - every address attribute is read with the browser's URL parser: http, https, mailto, tel and same-site paths pass,
 *   and data: passes only as an image source with an image media type;
 * - a link that opens a new tab gets rel="noopener noreferrer";
 * - data-* attributes and simple SVG pass (U7-c); contenteditable never does.
 * Markdown goes through one factory that escapes raw HTML first, so plain text (a wrong-filter member list such as
 * "<NA>, A & B, x<y", Marius's ruling 6(a)) is never handed to the cleaner as markup.
 * Remote images load as today: decision U7-a is held for Marius, and applyImagePolicy is the one place its rule goes.
 */

// The parser's helpers, for callers that pass options to renderHtml (MarkdownBox's table humanizer, StaticInfo's grid
// link hook).
export { attributesToProps, domToReact, Element, Text };
export type { DOMNode, HTMLReactParserOptions };

/** Elements that load another document, change the page, run code, or take input (an input is decided below). */
const FORBIDDEN_ELEMENTS = [
    'iframe',
    'frame',
    'frameset',
    'object',
    'embed',
    'applet',
    'portal',
    'base',
    'meta',
    'link',
    'style',
    'script',
    'noscript',
    'form',
    'button',
    'textarea',
    'select',
    'option',
    'optgroup',
    'datalist',
];

/** Attributes that carry a whole document, submit somewhere, or make the text editable. */
const FORBIDDEN_ATTRIBUTES = ['srcdoc', 'action', 'formaction', 'contenteditable'];

/** Attributes whose value the browser loads or navigates to. */
const ADDRESS_ATTRIBUTES = new Set([
    'href',
    'src',
    'xlink:href',
    'action',
    'formaction',
    'data',
    'poster',
    'background',
    'cite',
    'longdesc',
]);

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** The address as the browser's URL parser reads it (a relative address resolves against this page). */
function parseAddress(value: string): URL | null {
    try {
        return new URL(value, window.location.href);
    } catch {
        return null;
    }
}

/**
 * Whether an address may be loaded or followed: http, https, mailto, tel or a same-site path; data: only as an image
 * source whose media type is an image.
 */
export function isSafeAddress(value: string, asImageSource = false): boolean {
    const url = parseAddress(value);
    if (url === null) return false;
    if (SAFE_PROTOCOLS.has(url.protocol)) return true;
    return asImageSource && url.protocol === 'data:' && url.pathname.toLowerCase().startsWith('image/');
}

/**
 * Decision U7-a, HELD for Marius: the one place the remote-fetch rule goes (for example: load only from this site or as
 * data:image, others become a plain link). It sees every element, because a remote fetch is not only an image source:
 * - img src, and srcset on img and on picture's source (a list of addresses, outside the address check above);
 * - video poster;
 * - a style attribute's url() on any element (CSS, outside the address check above).
 * Until he rules it changes nothing, so every such fetch loads exactly as before.
 */
export function applyImagePolicy(element: globalThis.Element): void {
    void element;
}

let purifier: ReturnType<typeof DOMPurify> | null = null;

/** A purifier of our own, so no other caller's hooks or settings reach it, and ours reach no one else. */
function getPurifier(): ReturnType<typeof DOMPurify> {
    if (purifier !== null) return purifier;
    const instance = DOMPurify(window);
    instance.addHook('uponSanitizeAttribute', (node, data) => {
        const name = data.attrName.toLowerCase();
        if (!ADDRESS_ATTRIBUTES.has(name)) return;
        const asImageSource = node.nodeName.toLowerCase() === 'img' && name === 'src';
        if (!isSafeAddress(data.attrValue, asImageSource)) {
            data.keepAttr = false;
        }
    });
    instance.addHook('afterSanitizeAttributes', (node) => {
        if (node.nodeType !== 1) return;
        const element = node as globalThis.Element;
        const name = element.nodeName.toLowerCase();
        if (name === 'input' && (element.getAttribute('type') ?? '').toLowerCase() !== 'checkbox') {
            element.parentNode?.removeChild(element);
            return;
        }
        if (element.hasAttribute('target')) {
            element.setAttribute('rel', 'noopener noreferrer');
        }
        applyImagePolicy(element);
    });
    purifier = instance;
    return instance;
}

/** Markup in, markup out with nothing that runs, embeds a document, takes input or follows a script address. */
export function sanitizeHtml(html: string): string {
    return getPurifier().sanitize(html ?? '', {
        FORBID_TAGS: FORBIDDEN_ELEMENTS,
        FORBID_ATTR: FORBIDDEN_ATTRIBUTES,
        ADD_ATTR: ['target'],
    }) as string;
}

/** Cleaned markup as React nodes; the parser options (for example a replace hook) pass through. */
export function renderHtml(html: string, options?: HTMLReactParserOptions): ReturnType<typeof parse> {
    return parse(sanitizeHtml(html), options);
}

/** The one markdown factory: raw HTML in the text is escaped, and links follow the same address rule. */
function createMarkdown(): MarkdownIt {
    const markdown = new MarkdownIt({ html: false });
    markdown.validateLink = (url: string) => isSafeAddress(url, true);
    return markdown;
}

let markdownRenderer: MarkdownIt | null = null;

/** Markdown text to cleaned markup. The text itself is never cleaned as markup: markdown escapes it first. */
export function renderMarkdown(text: string): string {
    if (markdownRenderer === null) markdownRenderer = createMarkdown();
    return sanitizeHtml(markdownRenderer.render(text ?? ''));
}

const showdownConverters = new Map<string, showdown.Converter>();

/** Text in showdown's markdown dialect (welcome texts, learning articles, element descriptions) to cleaned markup. */
export function renderShowdown(text: string, options: showdown.ConverterOptions = {}): string {
    const key = JSON.stringify(options);
    let converter = showdownConverters.get(key);
    if (converter === undefined) {
        converter = new showdown.Converter(options);
        showdownConverters.set(key, converter);
    }
    return sanitizeHtml(converter.makeHtml(text ?? ''));
}

/**
 * Decision D3 (-63, 14:44 PT 10-03), the one place the rule lives: an address the app may open for a sign-in is
 * http or https on this page's own origin, or on the origin of the ROOTHOME value the server serves to the browser
 * (Guard.php:587; HOSTNAME followed by a slash on the hosts read 10-03). Every other address, a script or data
 * address, another site, a protocol-relative or user-info trick, is refused.
 */
export function isSameSiteHttpUrl(url: string, roothome?: string): boolean {
    const target = parseAddress(url);
    if (target === null || (target.protocol !== 'http:' && target.protocol !== 'https:')) return false;
    const origins = new Set([window.location.origin]);
    if (roothome) {
        try {
            const home = new URL(roothome);
            if (home.protocol === 'http:' || home.protocol === 'https:') origins.add(home.origin);
        } catch {
            // an unreadable ROOTHOME adds no origin
        }
    }
    return origins.has(target.origin);
}

/**
 * A name shown with the searched words marked, built as text nodes and spans, never as a markup string (the glossary
 * popup's search, GlossaryPopupLeftPanel). The match is the same as before: each occurrence of the search text as typed
 * (the popup lower-cases it). A user-driven list search, not a run-time decision over an answer's meaning.
 */
export function highlightText(text: string, pattern: string): React.ReactNode {
    if (!pattern) return text;
    const pieces = text.split(pattern);
    if (pieces.length === 1) return text;
    return pieces.flatMap((piece, index) =>
        index === 0
            ? [piece]
            : [React.createElement('span', { className: 'highlight', key: `highlight-${index}` }, pattern), piece],
    );
}
